import { DiffRange, LocalParagraphScore } from '../../db/indexedDb';
import { buildSynonymIndex, getSynonymWhitelist, type SynonymGroup } from './synonymWhitelist';

type ScoreInput = {
  reference: string;
  answer: string;
  keyUnits?: string[];
  synonymGroups?: SynonymGroup[];
};

const normalizeToken = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const canonicalToken = (value: string, synonymIndex: Record<string, string>) => {
  const normalized = normalizeToken(value);
  if (!normalized) return normalized;
  const direct = synonymIndex[normalized];
  if (direct) return direct;
  if (normalized.endsWith('s')) {
    const singular = normalized.slice(0, -1);
    return synonymIndex[singular] ?? singular;
  }
  return normalized;
};

const tokenize = (value: string) =>
  normalizeToken(value)
    .split(/\s+/)
    .filter(Boolean);

// 计算编辑距离（Levenshtein distance）
const levenshtein = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

// 检查是否为拼写相似（编辑距离小且相对长度合理）
const isTypo = (expected: string, actual: string): boolean => {
  const a = normalizeToken(expected);
  const b = normalizeToken(actual);
  if (!a || !b) return false;
  if (a === b) return false; // 完全相同不是 typo

  const maxLen = Math.max(a.length, b.length);
  const minLen = Math.min(a.length, b.length);

  // 太短的词不做模糊匹配（避免 "a" 匹配 "an"）
  if (minLen < 4) return false;

  const distance = levenshtein(a, b);

  // 允许的最大编辑距离：根据词长动态调整
  // 4-6字符: 1, 7-9字符: 2, 10+字符: 3
  const allowedDistance = minLen < 7 ? 1 : minLen < 10 ? 2 : 3;

  // 相似度必须 >= 70%
  const similarity = 1 - distance / maxLen;

  return distance <= allowedDistance && similarity >= 0.7;
};

const mergeRanges = (ranges: DiffRange[], next: DiffRange) => {
  const last = ranges[ranges.length - 1];
  if (last && last.kind === next.kind) {
    last.text = `${last.text} ${next.text}`.trim();
  } else {
    ranges.push(next);
  }
  return ranges;
};

const collectUnits = (reference: string, keyUnits: string[] = []): string[] => {
  const autoTokens = keyUnits.length === 0 ? tokenize(reference).filter((token) => token.length >= 4) : [];
  const merged = [...keyUnits, ...autoTokens].map((unit) => normalizeToken(unit)).filter(Boolean);
  return Array.from(new Set(merged));
};

const buildAnswerSets = (answer: string, synonymIndex: Record<string, string>) => {
  const rawTokens = answer
    .split(/\s+/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => ({ text, canon: canonicalToken(text, synonymIndex) }));

  const bag = new Map<string, number>();
  for (const token of rawTokens) {
    const count = bag.get(token.canon) ?? 0;
    bag.set(token.canon, count + 1);
  }

  const set = new Set(rawTokens.map((t) => t.canon));
  const rawSet = new Set(rawTokens.map((t) => normalizeToken(t.text)));
  return { rawTokens, bag, set, rawSet };
};

const buildDiffRanges = (
  reference: string,
  answerTokens: ReturnType<typeof buildAnswerSets>,
  synonymIndex: Record<string, string>
): DiffRange[] => {
  const ranges: DiffRange[] = [];
  const refTokens = reference
    .split(/\s+/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => ({ text, canon: canonicalToken(text, synonymIndex) }));

  // 创建答案词的副本用于模糊匹配消耗
  const answerBag = new Map(answerTokens.bag);
  // 追踪哪些答案词已被模糊匹配使用
  const usedForTypo = new Set<number>();

  for (const token of refTokens) {
    const available = answerBag.get(token.canon) ?? 0;
    if (available > 0) {
      // 精确匹配
      answerBag.set(token.canon, available - 1);
      mergeRanges(ranges, { text: token.text, kind: 'hit' });
    } else {
      // 尝试模糊匹配：找一个拼写相似的未使用答案词
      let typoMatch: { index: number; actual: string } | null = null;

      for (let i = 0; i < answerTokens.rawTokens.length; i++) {
        if (usedForTypo.has(i)) continue;
        const ansToken = answerTokens.rawTokens[i];
        const ansAvailable = answerBag.get(ansToken.canon) ?? 0;
        if (ansAvailable <= 0) continue;

        if (isTypo(token.text, ansToken.text)) {
          typoMatch = { index: i, actual: ansToken.text };
          break;
        }
      }

      if (typoMatch) {
        // 模糊匹配成功：标记为 typo（计入覆盖率）
        usedForTypo.add(typoMatch.index);
        const ansToken = answerTokens.rawTokens[typoMatch.index];
        const ansAvailable = answerBag.get(ansToken.canon) ?? 0;
        answerBag.set(ansToken.canon, ansAvailable - 1);
        mergeRanges(ranges, {
          text: token.text,
          kind: 'typo',
          actual: typoMatch.actual,
        });
      } else {
        // 没有匹配
        mergeRanges(ranges, { text: token.text, kind: 'missing' });
      }
    }
  }

  // Remaining tokens in answer are treated as extras for highlighting
  for (let i = 0; i < answerTokens.rawTokens.length; i++) {
    if (usedForTypo.has(i)) continue; // 已被用于模糊匹配，不算多写
    const token = answerTokens.rawTokens[i];
    const remaining = answerBag.get(token.canon) ?? 0;
    if (remaining > 0) {
      mergeRanges(ranges, { text: token.text, kind: 'extra' });
      answerBag.set(token.canon, remaining - 1);
    }
  }

  return ranges;
};

const findWeakSentences = (
  reference: string,
  answerSet: Set<string>,
  synonymIndex: Record<string, string>
): string[] => {
  const sentences = reference
    .split(/(?<=[.!?。！？])/)
    .map((s) => s.trim())
    .filter(Boolean);

  const weak: string[] = [];
  for (const sentence of sentences) {
    const tokens = tokenize(sentence).map((t) => canonicalToken(t, synonymIndex));
    if (!tokens.length) continue;
    const hits = tokens.filter((t) => answerSet.has(t)).length;
    const ratio = hits / tokens.length;
    if (ratio < 0.5) {
      weak.push(sentence.length > 120 ? `${sentence.slice(0, 118)}…` : sentence);
    }
  }
  return Array.from(new Set(weak));
};

export const scoreParagraphRecall = ({ reference, answer, keyUnits = [], synonymGroups }: ScoreInput): LocalParagraphScore => {
  const synonymIndex = buildSynonymIndex(synonymGroups ?? getSynonymWhitelist());
  const units = collectUnits(reference, keyUnits);
  const answerTokens = buildAnswerSets(answer, synonymIndex);

  const missingUnits: string[] = [];
  const synonymHits: Set<string> = new Set();

  for (const unit of units) {
    const unitTokens = tokenize(unit).map((t) => canonicalToken(t, synonymIndex));
    if (!unitTokens.length) continue;
    const hitCount = unitTokens.filter((t) => answerTokens.set.has(t)).length;
    const hit = hitCount / unitTokens.length >= 0.6;
    if (hit) {
      const rawTokens = tokenize(unit);
      if (rawTokens.some((t) => !answerTokens.rawSet.has(t) && answerTokens.set.has(canonicalToken(t, synonymIndex)))) {
        synonymHits.add(rawTokens.join(' '));
      }
    } else {
      missingUnits.push(unit);
    }
  }

  const diffRanges = buildDiffRanges(reference, answerTokens, synonymIndex);
  const weakSentences = findWeakSentences(reference, answerTokens.set, synonymIndex);

  // 覆盖率基于 diff 结果计算：(hit + typo) 词数 / 原文总词数
  // typo 也计入覆盖率，因为用户写出了正确的词（只是拼写有误）
  const hitCount = diffRanges
    .filter((r) => r.kind === 'hit' || r.kind === 'typo')
    .reduce((sum, r) => sum + r.text.split(/\s+/).length, 0);
  const refTokenCount = reference.split(/\s+/).filter(Boolean).length;
  const coverage = refTokenCount ? Math.round((hitCount / refTokenCount) * 100) : 0;

  return {
    coverage,
    missingUnits,
    weakSentences,
    diffRanges,
    synonymHits: Array.from(synonymHits),
  };
};

export const extractKeyUnits = (reference: string, extraUnits: string[] = []): string[] => collectUnits(reference, extraUnits);
